import { forwardRef, useMemo, useState } from "react";
import { tokens } from "@opendoor/tokens-studio/dist/bricks-next/tokens/Components";
import { getTokenValue, isIos, useComposedRefs, useProps } from "@tamagui/core";
import { PasswordHide, PasswordShow } from "../../../__generated__/icons";
import { getLineHeightTokens } from "../../../helpers/fontTokens";
import { isKeyOfObject } from "../../../helpers/utils";
import { Box, PressableBox } from "../../Box";
import { TrackableContainer } from "../../shared/TrackableContainer";
import { Text } from "../../Text";
import { useFrameFocus, useInputId, useInputName, useInputState } from "./hooks";
import { InputError } from "./InputError";
import { InputFrame } from "./InputFrame";
import { InputLabel } from "./InputLabel";
import { TextInputField } from "./TextInputField";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
const { textInput: textInputTokens } = tokens, PrefixSuffixText = ({
  size,
  state,
  children,
  ...props
}) => /* @__PURE__ */ jsx(
  Text,
  {
    tag: "span",
    typography: textInputTokens.typography.valueText[size],
    color: isKeyOfObject(state, textInputTokens.color.content.prefixSuffix.state) ? textInputTokens.color.content.prefixSuffix.state[state] : textInputTokens.color.content.prefixSuffix.default,
    ...props,
    children
  }
), TextInputBase = forwardRef(function({
  title,
  supportText,
  id,
  name,
  value,
  prefilledValue,
  placeholder,
  errorText,
  invalid,
  ariaLabel,
  ariaLabelledBy,
  disabled,
  required,
  prefix,
  suffix,
  multiline,
  onFocus,
  onBlur,
  onChangeText,
  analyticsName,
  trackImpression,
  textContentType,
  suggestion,
  onSuggestion,
  frameProps,
  children,
  ...propsIn
}, ref) {
  const { size = "medium", ...props } = useProps(propsIn), [showPassword, setShowPassword] = useState(textContentType === "password"), inputId = useInputId(id), elementRef = useInputName(name), combinedRefs = useComposedRefs(ref, elementRef), { inputRef, handleFramePress } = useFrameFocus(combinedRefs), { state, handleFocus, handleBlur } = useInputState({
    disabled,
    errorText,
    invalid,
    onBlur,
    onFocus
  }), heightProps = useMemo(() => getInputHeightProps({ multiline, size }), [multiline, size]), [internalValue, setInternalValue] = useState(value ?? prefilledValue ?? ""), handleTextChange = (text) => {
    setInternalValue(text), onChangeText?.(text);
  };
  return /* @__PURE__ */ jsxs(
    TrackableContainer,
    {
      analyticsName,
      trackImpression,
      gap: textInputTokens.padding.gap.textAndField[size],
      eventName: "inputViewEvent",
      children: [
        !!title && /* @__PURE__ */ jsx(
          InputLabel,
          {
            size,
            state,
            title,
            supportText,
            id: inputId,
            required
          }
        ),
        /* @__PURE__ */ jsxs(
          InputFrame,
          {
            size,
            state,
            onPress: handleFramePress,
            ...frameProps,
            flex: multiline ? 0 : 1,
            overflow: multiline ? "hidden" : void 0,
            children: [
              /* @__PURE__ */ jsxs(
                Box,
                {
                  flex: 1,
                  flexDirection: "row",
                  alignItems: multiline ? "flex-start" : "center",
                  gap: textInputTokens.padding.gap.prefixValueSuffix.default,
                  children: [
                    !!prefix && typeof prefix == "string" && /* @__PURE__ */ jsx(PrefixSuffixText, { size, state, children: prefix }),
                    /* @__PURE__ */ jsx(Box, { flexShrink: 1, flexGrow: 1, overflow: multiline ? "hidden" : void 0, children: /* @__PURE__ */ jsx(
                      TextInputField,
                      {
                        secureTextEntry: showPassword,
                        size,
                        color: isKeyOfObject(state, textInputTokens.color.content.valueText.state) ? textInputTokens.color.content.valueText.state[state] : textInputTokens.color.content.valueText.default,
                        id: inputId,
                        value,
                        defaultValue: prefilledValue,
                        placeholder,
                        disabled,
                        editable: !disabled,
                        onFocus: handleFocus,
                        onBlur: handleBlur,
                        onChangeText: handleTextChange,
                        ref: inputRef,
                        "aria-invalid": !!errorText || invalid,
                        "aria-required": required,
                        "aria-label": ariaLabel,
                        "aria-labelledby": ariaLabelledBy,
                        multiline,
                        textContentType,
                        scrollEnabled: multiline,
                        ...heightProps,
                        ...props
                      }
                    ) }),
                    !!suffix && typeof suffix == "string" && /* @__PURE__ */ jsxs(Fragment, { children: [
                      /* @__PURE__ */ jsx(Box, { opacity: 0, children: /* @__PURE__ */ jsx(PrefixSuffixText, { size, state, "aria-hidden": !0, children: suffix }) }),
                      /* @__PURE__ */ jsxs(
                        Box,
                        {
                          flexDirection: "row",
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          overflow: "hidden",
                          alignItems: "center",
                          gap: textInputTokens.padding.gap.prefixValueSuffix.default,
                          "aria-hidden": !0,
                          children: [
                            !!prefix && typeof prefix == "string" && /* @__PURE__ */ jsx(PrefixSuffixText, { size, state, "aria-hidden": !0, children: prefix }),
                            /* @__PURE__ */ jsx(
                              Text,
                              {
                                tag: "span",
                                typography: textInputTokens.typography.valueText[size],
                                numberOfLines: 1,
                                flexShrink: 1,
                                opacity: 0,
                                "aria-hidden": !0,
                                children: value ?? prefilledValue ?? internalValue
                              }
                            ),
                            /* @__PURE__ */ jsx(PrefixSuffixText, { size, state, "aria-hidden": !1, children: suffix })
                          ]
                        }
                      )
                    ] })
                  ]
                }
              ),
              textContentType === "password" && /* @__PURE__ */ jsx(
                PressableBox,
                {
                  "aria-label": "Toggle password visibility",
                  testID: "show-password-toggle",
                  analyticsName: "show-password-toggle",
                  onPress: () => {
                    setShowPassword(!showPassword);
                  },
                  children: showPassword ? /* @__PURE__ */ jsx(PasswordShow, {}) : /* @__PURE__ */ jsx(PasswordHide, {})
                }
              ),
              children
            ]
          }
        ),
        /* @__PURE__ */ jsx(InputError, { errorText }),
        suggestion ? /* @__PURE__ */ jsx(
          PressableBox,
          {
            "aria-label": "",
            analyticsName: "input-suggestion",
            onPress: onSuggestion,
            testID: "input-suggestion",
            justifyContent: "flex-start",
            children: /* @__PURE__ */ jsxs(
              Text,
              {
                tag: "span",
                mt: "$4x",
                color: "$contentAccentBrand",
                cursor: "pointer",
                typography: textInputTokens.typography.supportText.default,
                children: [
                  'Did you mean "',
                  suggestion,
                  '"?'
                ]
              }
            )
          }
        ) : null
      ]
    }
  );
}), getInputHeightProps = ({
  multiline,
  size
}) => {
  const props = {}, lineHeightToken = textInputTokens.typography.valueText[size];
  if (isIos && !multiline) {
    const lineHeightTokenVariable = getLineHeightTokens()?.[lineHeightToken];
    props.height = lineHeightTokenVariable && typeof lineHeightTokenVariable != "number" ? lineHeightTokenVariable?.val : void 0;
  } else
    props.lineHeight = lineHeightToken;
  if (multiline) {
    const numberOfLines = getTokenValue(
      tokens.textInput.text.row.multiline.default,
      "text"
    );
    props.numberOfLines = numberOfLines, props.textAlignVertical = "top", props.lineHeight = lineHeightToken;
  }
  return props;
};
export {
  TextInputBase
};
